import { BaseComponent } from '../base-component'
import {
  $el,
  $list,
  $elParent,
  transitionToPromise,
  getTargets,
  whenDefined,
} from '../../utils/utils'
import { isEscKey } from '../../utils/key'
import { getTemplateFirstChild } from '../../utils/dom'
import { type Nullable } from '../../types/common'
import { BodyElement } from '../body-element/body-element'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class ModalComponent extends BaseComponent {
  element: Nullable<HTMLElement>
  isOpen: boolean
  withOutsideClick: boolean
  skipTransitionWait: boolean

  constructor() {
    super()
    this.element = getTemplateFirstChild(
      $el(`[data-modal-template="${this.id}"]`, this),
    )
    this.withOutsideClick = this.hasAttribute('data-with-close-on-outside')
    this.skipTransitionWait = this.hasAttribute('data-skip-transition-wait')
  }

  mountComponent(): void {
    super.mountComponent()

    if (this.withOutsideClick) {
      this.addListener(window, 'click', this.handleOutsideClick)
    }

    if (this.isEditor) {
      if (!this.isSection) {
        this.hide(true)
      }
    }
  }

  handleModalFocusout = event => {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    const focusTarget = event.relatedTarget
    const isLastOpenedWindow = bodyElement.isLastOpenedWindow(this.id)
    const isfocusTargetOut = !focusTarget || !this.contains(focusTarget)

    if (isfocusTargetOut && isLastOpenedWindow && bodyElement.isUsingKeyboard) {
      const { firstTarget } = getTargets(this.element)

      firstTarget.focus()
    }
  }

  handleKeydown = (event: KeyboardEvent): void => {
    if (!this.hasAttribute('data-ignore-esc-key') && isEscKey(event)) {
      event.preventDefault()
      this.hide()
    }
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const isOverlay = $elParent(BODY_ELEMENT_SELECTOR, event.target)

    if (isOverlay && this.isOpen) {
      this.hide()
    }
  }

  async show(): Promise<void> {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    whenDefined('body-element').then(() => {
      bodyElement.addDialogWindow(this.id)
    })

    this.mount()

    await this.setOpenState(true)
    if (!this.skipTransitionWait) {
      await transitionToPromise(this)
    }

    const videos: HTMLVideoElement[] = $list('video[autoplay]', this.element)
    videos?.forEach(video => {
      video.play()
    })

    if (window.self === window.top) {
      const { firstTarget } = getTargets(this.element)
      firstTarget?.focus()
    }

    this.addListener(this, 'focusout', this.handleModalFocusout)
    this.addListener(window, 'keydown', this.handleKeydown)

    this.emit('show', {})
  }

  async hide(isInstant?: boolean): Promise<void> {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    whenDefined('body-element').then(() => {
      bodyElement.removeDialogWindow(this.id)
    })

    this.removeListener(window, 'keydown', this.handleKeydown)
    this.removeListener(this, 'focusout', this.handleModalFocusout)

    await this.setOpenState(false)

    if (this.skipTransitionWait || isInstant) {
      this.unmount()
    } else {
      await transitionToPromise(this)
      this.unmount()
    }

    this.emit('hide', {})
  }

  async toggle(): Promise<void> {
    if (this.isOpen) {
      this.hide()
    } else {
      this.show()
    }
  }

  mount(): void {
    this.replaceChildren(this.element)
  }

  unmount(): void {
    this.replaceChildren()
  }

  async setOpenState(isOpen: boolean): Promise<void> {
    this.isOpen = isOpen

    const withOverlay = this.hasAttribute('data-with-overlay')

    if (withOverlay) {
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
      const isImportant = this.hasAttribute('data-overlay-important')
      const hasCustomOpacity = this.hasAttribute('data-overlay-opacity')
      let styles = {}

      if (isImportant) {
        styles = {
          ...styles,
          zIndex: parseFloat(getComputedStyle(this).zIndex) - 1,
        }
      }

      if (hasCustomOpacity) {
        styles = {
          ...styles,
          '--gsc-overlay-opacity': +this.getAttribute('data-overlay-opacity'),
        }
      }

      whenDefined('body-element').then(() => {
        if (isOpen) {
          bodyElement.showOverlay(this.id, styles)
        } else {
          bodyElement.hideOverlay(this.id)
        }
      })
    }

    this.setVisible(isOpen)
  }

  setVisible(isOpened: boolean): void {
    this.classList.toggle('is-opened', isOpened)
  }

  get isSection(): boolean {
    return this.hasAttribute('is-section')
  }
}
